import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  ErrorHandler,
  Injector,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy,
  WritableSignal,
  inject,
  signal,
  untracked,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { applyEach, disabled, FieldTree, form, PathKind, required, SchemaPathTree, validate, ValidationError } from '@angular/forms/signals'

import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'

import { MatDatepicker } from '@angular/material/datepicker'
import { MatInput } from '@angular/material/input'
import { MatSelect } from '@angular/material/select'
import { MatSnackBar } from '@angular/material/snack-bar'

import { TimestampProviderBase } from '@lemon/core/service/timestamp-provider.interface'

import { arvonMuutokset, asetaArvo, nollaa } from '../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { FormValidationService } from '../../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../../_jaettu-angular/service/ladataan.service'

import { DateService } from '@lemon/core/service/date.service'

import { KirjanpitajanRooli } from '@lemon/core-firebase/lemonator/model/kirjanpitaja'
import { AlvIlmoitus, PaatilikartanProfiili } from 'app/_jaettu-lemonator/model/kirjanpito'
import { AsiakasUriService } from 'app/_jaettu-lemonator/service/asiakas-uri.service'
import { KirjanpitoUriService } from 'app/_jaettu-lemonator/service/kirjanpito-uri.service'
import {
  AlvIlmoitusjakso,
  AlvIlmoitusjaksoAikajaksolla,
  Asiakas,
  AsiakkaanKirjanpidonTyyppi,
  AsiakkaanKirjanpitajanVoimassaolojakso,
  AsiakkaanKuukausiruutukausi,
  KirjanpidonTyyppi,
  SelvittelyreskontranKytkentaTyoData,
  Tilikausi,
} from '../../_jaettu-lemonator/model/asiakas'
import { EI_KIRJANPITAJAA_AVAIN, KirjanpitajanNimitiedot } from '../../_jaettu-lemonator/model/kirjanpitaja'

import { LocalDate } from '@lemon/core/model/common'
import { DebugService } from 'app/_angular/service/debug.service'
import { AsiakasKopioija } from '../../_angular/service/asiakas/asiakas.kopioija'
import { AsiakasService, LokalisoituAlvIlmoitusjakso, LokalisoituYritysmuoto } from '../../_angular/service/asiakas/asiakas.service'
import { KirjanpitajaService } from '../../_angular/service/kirjanpitaja/kirjanpitaja.service'
import { KirjautunutKayttajaService } from '../../_angular/service/kirjautunut-kayttaja.service'

import { MatDialog } from '@angular/material/dialog'
import { AsiakkaanKirjanpidonPeruste, KirjanpidonPeruste, Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { InfoDialog, InfoDialogData } from 'app/_jaettu-angular/_components/info.dialog'

import { IFirestoreBatch } from 'app/_jaettu-angular/base-firebase.service'
import { AsiakkaanTilastojenPaivitysTyojonotiedot, AsiakkaidenTilikausienLuontityodata, KirjanpitoMerkitseTilikausiValmiiksiTyojonotiedot } from 'app/_jaettu-lemonator/model/tyojono'
import { AsiakasJaettuService } from 'app/_jaettu-lemonator/service/asiakas-jaettu.service'
import { KirjanpitajanTiimiService } from 'app/_jaettu-lemonator/service/kirjanpitajan-tiimi.service'
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of, Subject, Subscription } from 'rxjs'
import { map, switchMap, take, takeUntil } from 'rxjs/operators'
import { TilikartanProfiiliVaihdaDialog, TilikartanProfiiliVaihdaDialogData } from './tilikartan-profiili-vaihda.dialog'

interface OmaveroLomake {
  omaveroTilikausi: string | null
}

interface VoimassaoloRivi {
  voimassaoloAlkaa: Date | null
  kirjanpitaja: string | null
}

interface AlvIlmoitusjaksoRivi {
  jakso: AlvIlmoitusjakso | null
  voimassaAlkaen: Date | null
}

interface TilikausiRivi {
  lukittu: boolean
  synkkausLukittu: boolean
  alkaa: Date | null
  loppuu: Date | null
  tilikartanProfiili: PaatilikartanProfiili['avain'] | null
}

interface KirjanpidonTyyppiRivi {
  tyyppi: string | null
  alkaa: Date | null
  loppuu: Date | null
}

interface KirjanpidonPerusteRivi {
  peruste: string | null
  alkaa: Date | null
  loppuu: Date | null
}

interface KkruutujaksoRivi {
  alkaa: Date | null
  loppuu: Date | null
}

interface TasmaytysTilikausiRivi {
  tasmaytysAktiivinen: boolean
  tilikaudenJakso: string
}

interface VaihdekirjausTilikausiRivi {
  kirjauksetMuodostetaan: boolean
  tilikaudenJakso: string
}

interface KirjanpitotiedotLomake {
  yritysmuoto: Yritysmuoto | null
  lista: boolean | null
  alvIlmoitusjaksot: AlvIlmoitusjaksoRivi[]
  tilikaudet: TilikausiRivi[]
  voimassaolojaksot: VoimassaoloRivi[]
  voimassaolojaksotvara: VoimassaoloRivi[]
  kkruutukaudet: KkruutujaksoRivi[]
  tasmaytysTilikaudet: TasmaytysTilikausiRivi[]
  vaihdekirjausTilikaudet: VaihdekirjausTilikausiRivi[]
  vastaanottaja: string
  kirjanpidonTyypit: KirjanpidonTyyppiRivi[]
  kirjanpidonPerusteet: KirjanpidonPerusteRivi[]
  selvittelyLukituillaKkKaytossa: boolean | null
}

type Rivitaulukko =
  | 'alvIlmoitusjaksot'
  | 'tilikaudet'
  | 'voimassaolojaksot'
  | 'voimassaolojaksotvara'
  | 'kkruutukaudet'
  | 'tasmaytysTilikaudet'
  | 'vaihdekirjausTilikaudet'
  | 'kirjanpidonTyypit'
  | 'kirjanpidonPerusteet'

/** Rivin luontihetkellä määräytyvät asetukset, joita reaktiivinen lomake piti kontrollien käytöstilana ja validaattoreina. */
interface RivinAsetukset {
  pvmLukossa?: boolean
  tyyppiLukossa?: boolean
  lukossa?: boolean
  punakynaActivated?: boolean
  aikaisinMahdollinenPaivamaara?: Date
  tarkistus?: WritableSignal<number>
}

const TYHJA_VOIMASSAOLO: VoimassaoloRivi = { voimassaoloAlkaa: null, kirjanpitaja: null }

function signaaliVirheet(virheet: ValidationErrors | null): ValidationError[] | null {
  return virheet ? Object.keys(virheet).map((laji): ValidationError => ({ kind: laji })) : null
}

function rivinIndeksi(polku: readonly string[]): number {
  return Number(polku[polku.length - 2])
}

@Component({
  templateUrl: './kirjanpitotiedot.component.html',
  styleUrls: ['./kirjanpitotiedot.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakasAsetuksetKirjanpitotiedotComponent implements OnInit, OnDestroy, AfterContentChecked {
  @ViewChildren('ibanInput', { read: MatInput }) kaikkiIbanInputit: QueryList<MatInput>
  @ViewChildren('tilikausiAlkaaInput', { read: MatInput }) tilikausiAlkaaInputit: QueryList<MatInput>
  @ViewChildren('kkruutukausiAlkaaInput', { read: MatInput }) kkruutukausiAlkaaInputit: QueryList<MatInput>
  @ViewChildren('kirjanpitajaSelect', { read: MatSelect }) kirjanpitajaSelectit: QueryList<MatSelect>
  @ViewChildren('varakirjanpitajaSelect', { read: MatSelect }) varakirjanpitajaSelectit: QueryList<MatSelect>
  @ViewChildren('alvIlmoitusjaksoSelect', { read: MatSelect }) alvIlmoitusjaksoSelectit: QueryList<MatSelect>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()
  viimeisinAlvIlmoitusObservable: Observable<AlvIlmoitus | null>

  private readonly _injector = inject(Injector)
  private readonly _rivienAsetukset = new WeakMap<object, RivinAsetukset>()
  private readonly _rivienTilaukset = new Map<Rivitaulukko, Subscription[]>()
  private readonly _kirjanpidonTyypitNakyvissa = toSignal(
    combineLatest([
      this._kirjautunutKayttajaService.kirjanpitajaOnLuottoObservable,
      this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(map(kirjanpitajanTiedot => kirjanpitajanTiedot?.rooli === KirjanpitajanRooli.SUPER)),
    ]).pipe(map(([luotto, onkoSuper]) => !!luotto && onkoSuper)),
    { initialValue: false },
  )

  lomake = form(
    signal<KirjanpitotiedotLomake>({
      yritysmuoto: null,
      lista: null,
      alvIlmoitusjaksot: [],
      tilikaudet: [],
      voimassaolojaksot: [],
      voimassaolojaksotvara: [],
      kkruutukaudet: [],
      tasmaytysTilikaudet: [],
      vaihdekirjausTilikaudet: [],
      vastaanottaja: '',
      kirjanpidonTyypit: [],
      kirjanpidonPerusteet: [],
      selvittelyLukituillaKkKaytossa: false,
    }),
    polku => {
      required(polku.yritysmuoto)
      applyEach(polku.alvIlmoitusjaksot, rivi => {
        disabled(rivi.jakso, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).tyyppiLukossa)
        disabled(rivi.voimassaAlkaen, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).pvmLukossa)
        required(rivi.jakso)
        validate(rivi.jakso, ({ value, fieldTreeOf, pathKeys }) =>
          this._rivinAsetukset(fieldTreeOf(rivi)).punakynaActivated ? null : signaaliVirheet(this.edellinenAlvJaksoValidator(value(), rivinIndeksi(pathKeys()))),
        )
        required(rivi.voimassaAlkaen)
        validate(rivi.voimassaAlkaen, ({ value, fieldTreeOf, pathKeys }) => {
          const asetukset = this._rivinAsetukset(fieldTreeOf(rivi))
          if (asetukset.punakynaActivated) {
            return null
          }
          const index = rivinIndeksi(pathKeys())
          return signaaliVirheet({
            ...this.edellisenAlvjaksonKuukausiValidator(value(), index),
            ...this._getMinimumMonthValidator(value(), index, asetukset.aikaisinMahdollinenPaivamaara),
          })
        })
      })
      applyEach(polku.tilikaudet, rivi => {
        disabled(rivi.synkkausLukittu)
        disabled(rivi.alkaa, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).pvmLukossa)
        disabled(rivi.loppuu, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).pvmLukossa)
        disabled(rivi.tilikartanProfiili)
        required(rivi.alkaa)
        required(rivi.loppuu)
        validate(rivi, ({ value }) => signaaliVirheet(this._annaAlkaaEnnenLoppuaValidatorTilikausi(value())))
      })
      validate(polku.tilikaudet, ({ value }) => signaaliVirheet(this._oikeaAikajarjestysValidatorTilikausi(value())))
      applyEach(polku.voimassaolojaksot, rivi => this._voimassaolojaksonSaannot(rivi, 'kirjanpitajalle'))
      applyEach(polku.voimassaolojaksotvara, rivi => this._voimassaolojaksonSaannot(rivi, 'varakirjanpitajalle'))
      applyEach(polku.kkruutukaudet, rivi => {
        // Kentät luetaan seuraamatta: reaktiivisessa lomakkeessa kenttä tarkistettiin vain sen oman arvon muuttuessa
        // sekä muiden rivien muuttuessa 10 ms viiveellä (tarkistuslaskuri).
        required(rivi.alkaa)
        validate(rivi.alkaa, ({ value, fieldTreeOf, pathKeys }) => {
          const alkaa = value()
          this._rivinAsetukset(fieldTreeOf(rivi)).tarkistus?.()
          return untracked(() => {
            const index = rivinIndeksi(pathKeys())
            const rivit = this.lomake.kkruutukaudet().value()
            return signaaliVirheet({
              ...this._kkRuutuAlkaaEnnenLoppuaValidator(alkaa, rivit[index]?.loppuu),
              ...this._kkRuutuAlkaaEdellisenLoppumisenJalkeenValidator(alkaa, index, rivit),
            })
          })
        })
        validate(rivi.loppuu, ({ value, fieldTreeOf, pathKeys }) => {
          const loppuu = value()
          this._rivinAsetukset(fieldTreeOf(rivi)).tarkistus?.()
          return untracked(() => {
            const index = rivinIndeksi(pathKeys())
            const rivit = this.lomake.kkruutukaudet().value()
            return signaaliVirheet({
              ...this._kkRuutuAlkaaEnnenLoppuaValidator(rivit[index]?.alkaa, loppuu),
              ...this._kkRuutuEiLopuVaikkaSeuraavaValidator(loppuu, index, rivit),
            })
          })
        })
      })
      applyEach(polku.tasmaytysTilikaudet, rivi => {
        disabled(rivi, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
      })
      validoiReaktiivisesti(polku.vastaanottaja, kontrolli => this.validateEmail(kontrolli))
      // Pakollisuus tuli templaten required-attribuutista, joten se on voimassa vain, kun kentät näytetään.
      applyEach(polku.kirjanpidonTyypit, rivi => {
        required(rivi.tyyppi, { when: () => this._kirjanpidonTyypitNakyvissa() })
        required(rivi.alkaa, { when: () => this._kirjanpidonTyypitNakyvissa() })
        required(rivi.loppuu, { when: () => this._kirjanpidonTyypitNakyvissa() })
      })
      applyEach(polku.kirjanpidonPerusteet, rivi => {
        disabled(rivi.peruste, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
        disabled(rivi.alkaa, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
        disabled(rivi.loppuu)
        required(rivi.peruste)
        validate(rivi, ({ value }) => signaaliVirheet(this._annaAlkaaEnnenLoppuaValidatorPeruste(value())))
      })
      validate(polku.kirjanpidonPerusteet, ({ value }) => signaaliVirheet(this._katkeamatonAikajanaValidator(value())))
    },
  )
  omaveroLomake = form(signal<OmaveroLomake>({ omaveroTilikausi: null }), polku => {
    required(polku.omaveroTilikausi)
  })
  salesListaltaKirjanpitajalle = form(signal<VoimassaoloRivi>(TYHJA_VOIMASSAOLO), polku => {
    required(polku.voimassaoloAlkaa)
    validoiReaktiivisesti(polku.voimassaoloAlkaa, kontrolli => this._kuukaudenEkaPaivaValidator(kontrolli))
    required(polku.kirjanpitaja)
  })
  paivitaValidaatiot: any = null
  minMonth = new Date()
  asiakas: Asiakas = null
  private _originalAsiakas: Asiakas = null
  naytaSuper = false
  namename = 'asdf ' + Math.random()
  onkoDevaajaObservable: Observable<boolean>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  asiakasUriObservable: Observable<string>
  asiakasEncodedUriObservable: Observable<string>

  eiKirjanpitajaaAvain = EI_KIRJANPITAJAA_AVAIN

  yritysmuodot: LokalisoituYritysmuoto[] = null
  alvIlmoitusjaksot: LokalisoituAlvIlmoitusjakso[] = null

  omaverotasmaytysError: string = null
  kkruutuError: string = null

  naytaOmaveroTasmaytysObservable: Observable<boolean>
  naytaSuperObservable: Observable<boolean>
  naytaPoistuneetKirjanpitajatSubject: BehaviorSubject<boolean> = new BehaviorSubject(false)
  kirjanpitajatObservable: Observable<KirjanpitajanNimitiedot[]>
  omaveroTilikaudetObservable: Observable<Tilikausi[]>

  kaikkiKirjanpidonTyypit: { nimi: string; tunnus: KirjanpidonTyyppi }[] = [
    { nimi: 'Yhdenkertainen', tunnus: KirjanpidonTyyppi.YHDENKERTAINEN },
    { nimi: 'Kahdenkertainen', tunnus: KirjanpidonTyyppi.KAHDENKERTAINEN },
  ]
  kaikkiKirjanpidonPerusteet: { nimi: string; tunnus: KirjanpidonPeruste }[] = [
    { nimi: 'Suoriteperusteinen', tunnus: KirjanpidonPeruste.SUORITE },
    { nimi: 'Laskuperusteinen', tunnus: KirjanpidonPeruste.LASKU },
    { nimi: 'Maksuperusteinen', tunnus: KirjanpidonPeruste.MAKSU },
  ]
  kaikkiTilikartanProfiilit: { nimi: string; tunnus: PaatilikartanProfiili['avain'] }[] = [
    { nimi: 'Kevyt', tunnus: 'maksuperusteinen' },
    { nimi: 'Liikekirjuri', tunnus: 'liikekirjuri' },
  ]

  // minimiMuutokseDate: Date = new Date(1979, 11, 31)
  minimumAllowedDate: Date

  punakynaCanBeUsedObservable: Observable<boolean>
  punakynaActiveSubject: BehaviorSubject<boolean> = new BehaviorSubject(false)
  toggleMuokkaaMitaVain() {
    if (this.punakynaActiveSubject.value) {
      this.punakynaActiveSubject.next(false)
    } else {
      this.punakynaActiveSubject.next(true)
    }
  }
  readonly onkoDevaajaTaiKayttotuki = signal<boolean>(undefined)

  validateEmail: ValidatorFn = (ctrl: AbstractControl<string>): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    return Validators.email(ctrl)
  }
  readonly newTilikausiPending = signal(false)
  uusiTilikausiLomake: FieldTree<TilikausiRivi>
  private _tempKausi: Tilikausi

  private readonly SALES_KIRJANPITAJA_AVAIN = 't86aMdfTkicMvOiKGGjKm4m3Rwm1'
  salesKirjanpitaja = form(signal<string | null>(null), polku => {
    disabled(polku)
  })
  salesAlkaaPaiva: LocalDate
  readonly salesJakso = signal(false)
  readonly kirjanpitajaAlkaaMin = signal<Date>(null)
  readonly kirjanpitajaAlkaaMax = signal<Date>(null)

  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    // private route: ActivatedRoute,
    // private router: Router,
    private _dialog: MatDialog,
    private _asiakasService: AsiakasService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _dateService: DateService,
    private _asiakasKopioija: AsiakasKopioija,
    private _validationService: FormValidationService,
    private _errorHandler: ErrorHandler,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _kirjanpitajatService: KirjanpitajaService,
    private _timestampService: TimestampProviderBase,
    private _kirjanpitoUriService: KirjanpitoUriService,
    private _firebase: FirebaseLemonator,
    private _formValidationService: FormValidationService,
    private _asiakasJaettuService: AsiakasJaettuService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
    private _asiakasUriService: AsiakasUriService,
    private _debugService: DebugService,
  ) {
    this.punakynaCanBeUsedObservable = this._kirjautunutKayttajaService.kirjanpitajanAnnetaanMuokataMitaTahansaKirjauksiaObservable

    this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaTaiTukiObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(onkoDevaajaTaiKayttotuki => {
      this.onkoDevaajaTaiKayttotuki.set(onkoDevaajaTaiKayttotuki)
    })

    this.yritysmuodot = this._asiakasService.yritysmuodot
    this.alvIlmoitusjaksot = this._asiakasService.alvIlmoitusjaksot

    this.asiakas = this._asiakasKopioija.annaUusiAsiakas()
    this._originalAsiakas = this._asiakasKopioija.annaUusiAsiakas()

    this.naytaSuperObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(map(kirjanpitajanTiedot => kirjanpitajanTiedot?.rooli === KirjanpitajanRooli.SUPER))

    // Laske viimeisen 3kk aktiiviset kirjanpitäjät kerran ja cacheta tulos
    const viimeisen3kkAktiivisetKirjanpitajatObservable = this._kirjautunutKayttajaService.kirjanpitajanTiedotObservable.pipe(
      switchMap(async () => {
        const currentNumberDate = this._dateService.currentNumberDate()
        const kolmeKkSitten = this._dateService.lisaaKuukausiaNumero(currentNumberDate, -3)
        const tiimitViimeisen3kk = await this._kirjanpitajanTiimiService.annaTiimitPaivamaaraValille(kolmeKkSitten, currentNumberDate)
        const viimeisen3kkAktiivisetKirjanpitajat: Set<string> = new Set()
        for (const tiimi of tiimitViimeisen3kk) {
          for (const kp of tiimi.esihenkilot) {
            viimeisen3kkAktiivisetKirjanpitajat.add(kp.kirjanpitajaAvain)
          }
          for (const kp of tiimi.jasenet) {
            viimeisen3kkAktiivisetKirjanpitajat.add(kp.kirjanpitajaAvain)
          }
        }
        return viimeisen3kkAktiivisetKirjanpitajat
      }),
    )

    // Kaikki kirjanpitäjät lajiteltuna
    const kaikkiKirjanpitajatObservable = this._kirjanpitajatService.kirjanpitajienNimitiedotObservable.pipe(
      map(kirjanpitajat => {
        if (kirjanpitajat?.length) {
          return kirjanpitajat.sort((a, b) => {
            if (a.etunimi !== b.etunimi) {
              return a.etunimi.localeCompare(b.etunimi)
            }
            return a.sukunimi.localeCompare(b.sukunimi)
          })
        }
        return []
      }),
    )

    // Suodatetut kirjanpitäjät (3kk aktiiviset tai kaikki jos toggle on päällä)
    // Sisältää aina myös asiakkaan jo valitut kirjanpitäjät vaikka ne olisivat suodatettu pois
    this.kirjanpitajatObservable = combineLatest([
      kaikkiKirjanpitajatObservable,
      this.naytaPoistuneetKirjanpitajatSubject,
      viimeisen3kkAktiivisetKirjanpitajatObservable,
      this._asiakasService.nykyinenAsiakasObservable,
    ]).pipe(
      map(([kirjanpitajat, naytaPoistuneet, viimeisen3kkAktiiviset, asiakas]) => {
        if (naytaPoistuneet) {
          return kirjanpitajat
        }

        // Kerää kaikki asiakkaan jo valitut kirjanpitäjät
        const valitutKirjanpitajat = new Set<string>()
        if (asiakas?.kirjanpitajat) {
          for (const jakso of asiakas.kirjanpitajat) {
            if (jakso.kirjanpitajanAvain) {
              valitutKirjanpitajat.add(jakso.kirjanpitajanAvain)
            }
          }
        }
        if (asiakas?.kirjanpitajatvara) {
          for (const jakso of asiakas.kirjanpitajatvara) {
            if (jakso.kirjanpitajanAvain) {
              valitutKirjanpitajat.add(jakso.kirjanpitajanAvain)
            }
          }
        }

        // Suodata mutta sisällytä aina jo valitut kirjanpitäjät ja "Poistuneet Asiakkaat"
        return kirjanpitajat.filter(
          kp => viimeisen3kkAktiiviset.has(kp.avain) || valitutKirjanpitajat.has(kp.avain) || kp.avain === 'uekRGWgeLqecXk0Gh8el0s85SEt1', // Poistunee Asiakkaat / vanha Taneli Testinen
        )
      }),
    )
  }

  toggleNaytaPoistuneetKirjanpitajat() {
    this.naytaPoistuneetKirjanpitajatSubject.next(!this.naytaPoistuneetKirjanpitajatSubject.value)
  }

  private _rivinAsetukset(rivi: object): RivinAsetukset {
    return this._rivienAsetukset.get(rivi) ?? {}
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  naytetaankoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    const tila = kentta()
    return this.onkoVirhe(kentta, laji) && (tila.dirty() || tila.touched())
  }

  // Rivin lisäys ja poisto eivät lähetä muiden rivien kenttien arvoja uudelleen, kuten FormArrayn push, removeAt ja clear.
  private _lisaaRivi<T>(nimi: Rivitaulukko, taulukko: FieldTree<T[]>, rivi: T, asetukset: RivinAsetukset = {}) {
    const rivit = taulukko().value()
    taulukko().value.set([...rivit, rivi])
    const kentta = taulukko[rivit.length]
    this._rivienAsetukset.set(kentta, asetukset)
    const tilaukset = new Subscription()
    if (!this._rivienTilaukset.has(nimi)) {
      this._rivienTilaukset.set(nimi, [])
    }
    this._rivienTilaukset.get(nimi).push(tilaukset)
    const muutokset = <K>(alikentta: FieldTree<K>, kasittelija: (arvo: K) => void) => {
      tilaukset.add(arvonMuutokset(alikentta, { injector: this._injector }).pipe(takeUntil(this._ngUnsubscribe)).subscribe(kasittelija))
    }
    return { kentta, muutokset }
  }

  private _poistaRivi<T>(nimi: Rivitaulukko, taulukko: FieldTree<T[]>, index: number) {
    const tilaukset = this._rivienTilaukset.get(nimi) ?? []
    tilaukset[index]?.unsubscribe()
    tilaukset.splice(index, 1)
    taulukko().value.set(
      taulukko()
        .value()
        .filter((_, i) => i !== index),
    )
  }

  private _tyhjennaRivit<T>(nimi: Rivitaulukko, taulukko: FieldTree<T[]>) {
    for (const tilaukset of this._rivienTilaukset.get(nimi) ?? []) {
      tilaukset.unsubscribe()
    }
    this._rivienTilaukset.set(nimi, [])
    taulukko().value.set([])
  }

  private _rivinIndeksi<T>(taulukko: FieldTree<T[]>, rivi: object): number {
    return [...taulukko].findIndex(kentta => kentta === rivi)
  }

  ngAfterContentChecked() {
    // this.fieldErrors = this.validationService.updateValidationStatus('asiakas', this.form)
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnInit() {
    const d = new Date()
    d.setDate(1)
    this.minMonth = this._dateService.lisaaKuukausia(d, 1)

    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this._ngUnsubscribe))

    muutokset(this.lomake.yritysmuoto).subscribe(value => {
      this.asiakas.yritysmuoto = value
    })

    muutokset(this.lomake.lista).subscribe(value => {
      this.asiakas.uudessaListassa = !!value
    })

    muutokset(this.lomake.selvittelyLukituillaKkKaytossa).subscribe(value => {
      this.asiakas.selvittelyLukituillaKkKaytossa = !!value
    })

    this.viimeisinAlvIlmoitusObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      switchMap(asiakas => {
        if (asiakas) {
          const alvilmoituksetUri = this._kirjanpitoUriService.annaAlvIlmoituksenCollectionUri(asiakas.avain)
          return this._firebase.firestoreCollection<AlvIlmoitus>(alvilmoituksetUri).orderBy('tunniste', 'desc').limit(1).listen()
        }
        return of<AlvIlmoitus[]>([])
      }),
      map(edelliset => {
        if (edelliset && edelliset.length > 0) {
          return edelliset[0]
        }
        return null
      }),
    )

    this.omaveroTilikaudetObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(map(asiakas => asiakas?.tilikaudet?.filter(tilikausi => !tilikausi.lukittu && tilikausi.avain) ?? []))

    this.naytaOmaveroTasmaytysObservable = this._asiakasService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        return asiakas && (asiakas.yritysmuoto === Yritysmuoto.OSAKEYHTIO || asiakas.yritysmuoto === Yritysmuoto.OSUUSKUNTA || asiakas.yritysmuoto === Yritysmuoto.TOIMINIMI)
      }),
    )

    combineLatest([this._asiakasService.nykyinenAsiakasObservable, this.viimeisinAlvIlmoitusObservable, this.punakynaActiveSubject])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([a, viimeisinAlvIlmoitus, punakynaActivated]) => {
        if (a) {
          this._originalAsiakas = a
          this.asiakas = this._asiakasKopioija.kopioiAsiakas(a)
          this._alustaLomakkeenTiedot(this.asiakas, viimeisinAlvIlmoitus, punakynaActivated)
          this._changeDetectorRef.markForCheck()
        }
      })

    this.onkoDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnLuottoObservable

    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.asiakasUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(avainTiedot => {
        if (avainTiedot?.avain) {
          return this._asiakasUriService.annaAsiakasUri(avainTiedot.avain)
        }
        return ''
      }),
    )
    this.asiakasEncodedUriObservable = this.asiakasUriObservable.pipe(
      map(uri => {
        return this._debugService.createFirestoreLink(uri)
      }),
    )
  }

  private _onkoEnsimmainenSalesJakso(asiakas: Asiakas): boolean {
    if (!asiakas?.kirjanpitajat?.length || asiakas.kirjanpitajat.length !== 1) {
      return false
    }
    return asiakas.kirjanpitajat[0].kirjanpitajanAvain === this.SALES_KIRJANPITAJA_AVAIN
  }

  private _annaLaskutuksenAloituskuukausi(asiakas: Asiakas): LocalDate | null {
    const ekaHinta = (asiakas?.hinnat ?? []).filter(hinta => hinta.hinta > 0).sort((a, b) => (this._dateService.compareLocalDates(a.voimassaAlkaen, '<', b.voimassaAlkaen) ? -1 : 1))[0]
    if (!ekaHinta) {
      return null
    }
    return { year: ekaHinta.voimassaAlkaen.year, month: ekaHinta.voimassaAlkaen.month, day: 1 }
  }

  valitseKirjanpitajanAlkukuukausi(valittuKuukausi: Date, datepicker: MatDatepicker<Date>, jakso: FieldTree<VoimassaoloRivi>) {
    const kuunAlku = new Date(valittuKuukausi.getFullYear(), valittuKuukausi.getMonth(), 1)
    asetaArvo(jakso.voimassaoloAlkaa, kuunAlku)
    jakso.voimassaoloAlkaa().markAsTouched()
    datepicker.close()
  }

  private _voimassaolojaksonSaannot(rivi: SchemaPathTree<VoimassaoloRivi, PathKind.Item>, vara: 'varakirjanpitajalle' | 'kirjanpitajalle') {
    disabled(rivi, ({ fieldTreeOf }) => !!this._rivinAsetukset(fieldTreeOf(rivi)).lukossa)
    required(rivi.voimassaoloAlkaa)
    // Edellinen jakso luetaan seuraamatta, koska reaktiivinen lomake tarkisti päivän vain sen oman arvon muuttuessa.
    validate(rivi.voimassaoloAlkaa, ({ value, pathKeys }) => {
      const arvo = value()
      return untracked(() => signaaliVirheet(this._getkirjanpitajanEdellinenKuukausiValidator(arvo, rivinIndeksi(pathKeys()), vara)))
    })
    required(rivi.kirjanpitaja)
  }

  private _getkirjanpitajanEdellinenKuukausiValidator(arvo: Date, index: number, vara: 'varakirjanpitajalle' | 'kirjanpitajalle'): ValidationErrors | null {
    // console.log('PVM', arvo)
    if (arvo) {
      const kaikki = vara === 'varakirjanpitajalle' ? this.lomake.voimassaolojaksotvara().value() : this.lomake.voimassaolojaksot().value()
      if (index > 0) {
        // console.log('GOT index', index)
        const edellinen = kaikki[index - 1]
        if (edellinen) {
          if (edellinen.voimassaoloAlkaa) {
            const dateToCheck = this._dateService.dateToLocalDate(arvo)
            const previousDate = this._dateService.dateToLocalDate(edellinen.voimassaoloAlkaa)
            // console.log('Validoidaan nykyinen: ', dateToCheck, 'Validoidaan edellinen:', previousDate, this._dateService.compareLocalDates(dateToCheck, '<=', previousDate))
            if (this._dateService.compareLocalDates(dateToCheck, '<=', previousDate)) {
              return { edellinen: true }
            }
          }
        }
      }
    }
    return null
  }

  get onkoEnsimmaisenKirjanpitajanAlkuVirheellinen(): boolean {
    return !this.salesListaltaKirjanpitajalle.voimassaoloAlkaa().valid()
  }

  get onkoSiirtoEstetty(): boolean {
    const kirjanpitajanAvain = this.salesListaltaKirjanpitajalle.kirjanpitaja().value()
    return !kirjanpitajanAvain || kirjanpitajanAvain === this.SALES_KIRJANPITAJA_AVAIN
  }

  private _kuukaudenEkaPaivaValidator: ValidatorFn = (control: AbstractControl<Date>) => {
    if (control.value) {
      const paiva = this._dateService.dateToLocalDate(control.value)
      if (paiva.day !== 1) {
        return { eiKuukaudenAlku: true }
      }
    }
    return null
  }

  private _annaAikaisinMahdollinenPaivamaara(viimeisinAlvIlmoitus: AlvIlmoitus): Date {
    if (viimeisinAlvIlmoitus) {
      const asDate = this._dateService.localDateToDate({ year: viimeisinAlvIlmoitus.endDate.year, month: viimeisinAlvIlmoitus.endDate.month, day: 1 })
      return this._dateService.lisaaKuukausia(asDate, 1)
    }
    return new Date(1979, 11, 31)
  }

  private _alustaLomakkeenTiedot(asiakas: Asiakas, viimeisinAlvIlmoitus: AlvIlmoitus, punakynaActivated: boolean) {
    asetaArvo(this.lomake.yritysmuoto, asiakas ? (asiakas.yritysmuoto ?? null) : null)

    const localMonth = this._dateService.numberToLocalMonth(asiakas.uudessaKirjanpitoAppissaAlkaen)
    const asDate = localMonth ? this._dateService.localDateToDate({ year: localMonth.year, month: localMonth.month, day: 15 }) : null
    // this.form.get('uudessaListassaAlkaen').setValue(asDate)
    asetaArvo(this.lomake.lista, asiakas.uudessaListassa ?? null)
    asetaArvo(this.lomake.selvittelyLukituillaKkKaytossa, this._asiakasJaettuService.onkoSelvittelyLukituillaKaytossa(asiakas))

    // this.form.get('alvIlmoitusjakso').setValue(asiakas ? asiakas.alvIlmoitusjakso : null)

    this._tyhjennaRivit('alvIlmoitusjaksot', this.lomake.alvIlmoitusjaksot)
    if (asiakas && asiakas.alvIlmoitusjaksot) {
      const aikaisinMahdollinenPaivamaara = this._annaAikaisinMahdollinenPaivamaara(viimeisinAlvIlmoitus)
      let index = 0
      for (const alvIlmoitusjakso of asiakas.alvIlmoitusjaksot) {
        this._lisaaAlvIlmoitusjaksorivi(alvIlmoitusjakso, index, asiakas.alvIlmoitusjaksot, viimeisinAlvIlmoitus, false, true, aikaisinMahdollinenPaivamaara, punakynaActivated)
        index++
      }
    }

    this._tyhjennaRivit('tilikaudet', this.lomake.tilikaudet)
    if (asiakas && asiakas.tilikaudet) {
      asiakas.tilikaudet.sort((a, b) => {
        if (a?.alkaa && b?.alkaa) {
          return this._dateService.compareLocalDates(a.alkaa, '<', b.alkaa) ? -1 : 1
        }
        return 0
      })
      for (const tilikausi of asiakas.tilikaudet) {
        this._lisaaTilikausirivi(tilikausi, false, true)
      }
    }

    this.salesJakso.set(this._onkoEnsimmainenSalesJakso(asiakas))
    nollaa(this.salesListaltaKirjanpitajalle, TYHJA_VOIMASSAOLO)
    if (this.salesJakso()) {
      this.salesAlkaaPaiva = asiakas.siirrettyJakolistalta?.salesAlkaa ?? asiakas.kirjanpitajat[0].voimassaoloAlkaa
      asetaArvo(this.salesKirjanpitaja, asiakas.kirjanpitajat[0].kirjanpitajanAvain ?? null)
      const ekaLaskutuskuukausi = this._annaLaskutuksenAloituskuukausi(asiakas)
      const kuluvaKuukausi = this._dateService.currentLocalMonth()
      const kuluvaDate = this._dateService.localDateToDate({ year: kuluvaKuukausi.year, month: kuluvaKuukausi.month, day: 1 })
      if (ekaLaskutuskuukausi) {
        this.kirjanpitajaAlkaaMax.set(this._dateService.localDateToDate(ekaLaskutuskuukausi))
        this.kirjanpitajaAlkaaMin.set(null)
      } else {
        this.kirjanpitajaAlkaaMin.set(kuluvaDate)
        this.kirjanpitajaAlkaaMax.set(kuluvaDate)
      }
    }

    this._tyhjennaRivit('voimassaolojaksot', this.lomake.voimassaolojaksot)
    if (asiakas && asiakas.kirjanpitajat) {
      for (const jakso of asiakas.kirjanpitajat) {
        const lukitseVanhat = !this.salesJakso()
        this._lisaaKirjanpitajanVoimassaolojaksorivi(jakso, false, lukitseVanhat, 'kirjanpitajalle')
      }
    }

    if (this.salesJakso() && this.kirjanpitajaAlkaaMax()) {
      const kuluvaKuukausi = this._dateService.currentLocalMonth()
      const kuluvaDate = this._dateService.localDateToDate({ year: kuluvaKuukausi.year, month: kuluvaKuukausi.month, day: 1 })
      const oletusAlkaa = kuluvaDate > this.kirjanpitajaAlkaaMax() ? this.kirjanpitajaAlkaaMax() : kuluvaDate
      asetaArvo(this.salesListaltaKirjanpitajalle.voimassaoloAlkaa, oletusAlkaa)
    }

    this._tyhjennaRivit('voimassaolojaksotvara', this.lomake.voimassaolojaksotvara)
    if (asiakas && asiakas.kirjanpitajatvara) {
      for (const jakso of asiakas.kirjanpitajatvara) {
        this._lisaaKirjanpitajanVoimassaolojaksorivi(jakso, false, true, 'varakirjanpitajalle')
      }
    }

    this._tyhjennaRivit('kkruutukaudet', this.lomake.kkruutukaudet)
    if (asiakas && asiakas.kkruutujenKaudet) {
      for (const kausi of asiakas.kkruutujenKaudet) {
        this._lisaaKkRuudunKausirivi(kausi, false, true)
      }
    }

    this._tyhjennaRivit('kkruutukaudet', this.lomake.kkruutukaudet)
    if (asiakas && asiakas.kkruutujenKaudet) {
      for (const kausi of asiakas.kkruutujenKaudet) {
        this._lisaaKkRuudunKausirivi(kausi, false, true)
      }
    }

    this._tyhjennaRivit('tasmaytysTilikaudet', this.lomake.tasmaytysTilikaudet)
    for (const tilikausi of asiakas?.tilikaudet ?? []) {
      this._lisaaTasmaytysTilikausirivi(tilikausi, !!(tilikausi.lukittu || tilikausi.automationLocked))
    }

    // Lukitunkin tilikauden lippu pitää voida vaihtaa: se ohjaa seuraavalle tilikaudelle muodostettavia vaihdekirjauksia, ei lukitun tilikauden omia kirjauksia.
    this._tyhjennaRivit('vaihdekirjausTilikaudet', this.lomake.vaihdekirjausTilikaudet)
    for (const tilikausi of asiakas?.tilikaudet ?? []) {
      this._lisaaVaihdekirjausTilikausirivi(tilikausi)
    }

    this._tyhjennaRivit('kirjanpidonTyypit', this.lomake.kirjanpidonTyypit)
    if (asiakas?.kirjanpidonTyypit) {
      for (const tyyppi of asiakas.kirjanpidonTyypit) {
        this._lisaaKirjanpidonTyyppirivi(tyyppi)
      }
    }

    this._tyhjennaRivit('kirjanpidonPerusteet', this.lomake.kirjanpidonPerusteet)
    if (asiakas?.kirjanpidonPerusteet) {
      let index = 0
      for (const peruste of asiakas.kirjanpidonPerusteet) {
        this._lisaaKirjanpidonPerusterivi(peruste, index < 1)
        index++
      }
    }
  }

  private _getMinimumMonthValidator(arvo: Date, index: number, aikaisinMahdollinenPaivamaara: Date): ValidationErrors | null {
    if (arvo) {
      const minDate = this._annaMinimiMuutokseDate(index, aikaisinMahdollinenPaivamaara)
      if (this._dateService.kuukausiaValissa(arvo, minDate) < 0) {
        this.minimumAllowedDate = minDate
        return { min: true }
      }
    }
    return null
  }

  private annaUusiKirjanpitajanVoimassaolojakso(): AsiakkaanKirjanpitajanVoimassaolojakso {
    return {
      kirjanpitajanAvain: null,
      voimassaoloAlkaa: null,
    }
  }

  poistaKirjanpitajanVoimassaolojakso(rivi: FieldTree<VoimassaoloRivi>) {
    const index = this._rivinIndeksi(this.lomake.voimassaolojaksot, rivi)
    if (rivi.voimassaoloAlkaa().disabled() || index < 1) {
      return
    }
    this._poistaRivi('voimassaolojaksot', this.lomake.voimassaolojaksot, index)
    this.asiakas.kirjanpitajat.splice(index, 1)
  }

  async kaynnistaOmaverotasmaytysPuppeteer() {
    this._snackbar.open('Puppeteer-pohjainen Omaverotäsmäytys ei ole enää käytössä.', null, { duration: 5000 })
  }

  private kaynnistaOmaverotasmaytysKaynnissa = false
  async kaynnistaOmaverotasmaytys() {
    try {
      if (this.asiakas.suomiFiValtuutusPuuttuu) {
        this.omaverotasmaytysError = 'Asiakkaan Suomi.fi -valtuutus puuttuu. Täsmäytystä ei voi suorittaa.'
        return
      }
      this.omaverotasmaytysError = null

      if (!this.omaveroLomake.omaveroTilikausi().value()) {
        this._validationService.merkitseKokoLomakeKosketuksi(this.omaveroLomake)
        return
      }

      if (this.kaynnistaOmaverotasmaytysKaynnissa) {
        return
      }

      this.kaynnistaOmaverotasmaytysKaynnissa = false

      this._ladataanService.aloitaLataaminen()

      const promise = this._firebase.functionsCall<{ asiakasAvain: string; tilikausiAvain: string }, void>('integraatiotOmaveroTasmaytysKaynnistaYksittainenTilikausi', {
        asiakasAvain: this.asiakas.avain,
        tilikausiAvain: this.omaveroLomake.omaveroTilikausi().value(),
      })

      await promise

      this._snackbar.open('Omaverotäsmäytys tilikaudelle aloitettiin. Tavallisesti se kestää n. 5 minuuttia.', null, { duration: 5000 })
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this.kaynnistaOmaverotasmaytysKaynnissa = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  lisaaKirjanpitajanVoimassaolojakso() {
    const jakso = this.annaUusiKirjanpitajanVoimassaolojakso()

    if (!this.asiakas.kirjanpitajat) {
      this.asiakas.kirjanpitajat = []
    }
    this.asiakas.kirjanpitajat.push(jakso)
    this._lisaaKirjanpitajanVoimassaolojaksorivi(jakso, true, false, 'kirjanpitajalle')
  }

  poistaKirjanpitajanVoimassaolojaksovara(rivi: FieldTree<VoimassaoloRivi>) {
    if (rivi.voimassaoloAlkaa().disabled()) {
      return
    }
    const index = this._rivinIndeksi(this.lomake.voimassaolojaksotvara, rivi)
    if (index > -1) {
      this._poistaRivi('voimassaolojaksotvara', this.lomake.voimassaolojaksotvara, index)
      this.asiakas.kirjanpitajatvara.splice(index, 1)
    }
  }

  lisaaKirjanpitajanVoimassaolojaksovara() {
    const jakso = this.annaUusiKirjanpitajanVoimassaolojakso()

    if (!this.asiakas.kirjanpitajatvara) {
      this.asiakas.kirjanpitajatvara = []
    }
    this.asiakas.kirjanpitajatvara.push(jakso)
    this._lisaaKirjanpitajanVoimassaolojaksorivi(jakso, true, false, 'varakirjanpitajalle')
  }

  private _lisaaKirjanpitajanVoimassaolojaksorivi(jakso: AsiakkaanKirjanpitajanVoimassaolojakso, focus: boolean, lukitseVanhat: boolean, vara: 'varakirjanpitajalle' | 'kirjanpitajalle') {
    // const d = new Date()
    // d.setDate(2)
    // console.log('PÄIVÄ', d.toISOString(), this._dateService.lisaaKuukausia(d, 1).toISOString())

    const paiva = this._dateService.localDateToDate(jakso.voimassaoloAlkaa)
    // const lukossa = false
    const lukossa = lukitseVanhat && this._dateService.compareLocalDates(jakso.voimassaoloAlkaa, '<', this._dateService.currentLocalDate())
    const nimi = vara === 'varakirjanpitajalle' ? 'voimassaolojaksotvara' : 'voimassaolojaksot'
    const { kentta, muutokset } = this._lisaaRivi(nimi, this.lomake[nimi], { voimassaoloAlkaa: paiva, kirjanpitaja: jakso.kirjanpitajanAvain ?? null }, { lukossa })

    muutokset(kentta.voimassaoloAlkaa, (value: Date) => {
      jakso.voimassaoloAlkaa = this._dateService.dateToLocalDate(value)
    })

    muutokset(kentta.kirjanpitaja, value => {
      jakso.kirjanpitajanAvain = value
    })

    if (focus) {
      setTimeout(() => {
        if (vara === 'varakirjanpitajalle') {
          if (this.varakirjanpitajaSelectit.last) {
            this.varakirjanpitajaSelectit.last.focus()
            this._changeDetectorRef.markForCheck()
          }
        } else {
          if (this.kirjanpitajaSelectit.last) {
            this.kirjanpitajaSelectit.last.focus()
            this._changeDetectorRef.markForCheck()
          }
        }
      }, 10)
    }
  }

  private _annaKkruutujakso(previous?: AsiakkaanKuukausiruutukausi): AsiakkaanKuukausiruutukausi {
    return {
      avain: this._firebase.firestoreCreateId(),
      luoja: 'lemonator_system',
      luotu: this._timestampService.now(),
      paivittaja: 'lemonator_system',
      poistettu: false,
      paivitetty: this._timestampService.now(),
      alkaa: previous?.loppuu ? this._dateService.lisaaPaiviaPaikallinen(previous.loppuu, 1) : null,
      loppuu: null,
    }
  }

  lisaaKkruutujakso() {
    if (this.asiakas.kkruutujenKaudet?.find(kkr => !kkr.loppuu)) {
      this.kkruutuError = 'Päätä edellinen jakso ennen uuden lisäämistä'
      return
    }

    this.kkruutuError = null

    const edellinenKausi = this.asiakas.kkruutujenKaudet?.sort((a, b) => this._dateService.localDateToNumber(b.alkaa) - this._dateService.localDateToNumber(a.alkaa))?.[0]
    const kausi = this._annaKkruutujakso(edellinenKausi)
    this._lisaaKkRuudunKausirivi(kausi, true, false)

    if (!this.asiakas.kkruutujenKaudet) {
      this.asiakas.kkruutujenKaudet = []
    }
    this.asiakas.kkruutujenKaudet.push(kausi)
  }

  private _kkRuutuAlkaaEnnenLoppuaValidator(a: Date, l: Date): ValidationErrors | null {
    if (l && a && a >= l) {
      return { loppuEnnenAlkua: true }
    }
    return null
  }

  private _kkRuutuAlkaaEdellisenLoppumisenJalkeenValidator(a: Date, nykyinenIndex: number, rivit: KkruutujaksoRivi[]): ValidationErrors | null {
    if (nykyinenIndex > 0) {
      const l = rivit[nykyinenIndex - 1].loppuu
      if (l && a && a <= l) {
        return { alkuEnnenEdellisenLoppua: true }
      }
    }
    return null
  }

  private _kkRuutuEiLopuVaikkaSeuraavaValidator(loppuu: Date, nykyinenIndex: number, rivit: KkruutujaksoRivi[]): ValidationErrors | null {
    if (nykyinenIndex + 1 < rivit.length && !loppuu) {
      return { loppuuPuuttuu: true }
    }
    return null
  }

  private _lisaaKkRuudunKausirivi(kausi: AsiakkaanKuukausiruutukausi, focus: boolean, lukitseVanhat: boolean) {
    const lukossa = false // lukitseVanhat // && this._dateService.compareLocalDates(kausi.voimassaoloAlkaa, '<', this._dateService.currentLocalDate())

    const alkaa = this._dateService.localDateToDate(kausi.alkaa)
    const loppuu = this._dateService.localDateToDate(kausi.loppuu)

    const { kentta, muutokset } = this._lisaaRivi('kkruutukaudet', this.lomake.kkruutukaudet, { alkaa, loppuu }, { lukossa, tarkistus: signal(0) })

    const tarkistaMuutRivit = () => {
      setTimeout(() => {
        for (const muu of this.lomake.kkruutukaudet) {
          if (muu !== kentta) {
            this._rivinAsetukset(muu).tarkistus?.update(kerta => kerta + 1)
            muu().markAsTouched()
          }
        }
      }, 10)
    }

    muutokset(kentta.alkaa, (value: Date) => {
      kausi.alkaa = this._dateService.dateToLocalDate(value)
      tarkistaMuutRivit()
    })

    muutokset(kentta.loppuu, value => {
      kausi.loppuu = this._dateService.dateToLocalDate(value)
      tarkistaMuutRivit()
    })

    if (focus) {
      setTimeout(() => {
        if (this.kkruutukausiAlkaaInputit.last) {
          this.kkruutukausiAlkaaInputit.last.focus()
          this._changeDetectorRef.markForCheck()
        }
      }, 50)
    }
  }

  private _onkoPaallekkainToisenTilikaudenKanssaValidator(arvo: TilikausiRivi): ValidationErrors | null {
    if (!arvo) {
      return null
    }
    const alkaa = this._dateService.dateToLocalDate(arvo.alkaa)
    const loppuu = this._dateService.dateToLocalDate(arvo.loppuu)
    if (!alkaa || !loppuu) {
      return null
    }
    if (
      this.asiakas.tilikaudet?.some(
        tk =>
          this._dateService.onkoLocalDateKahdenValissa(alkaa, tk.alkaa, tk.loppuu) ||
          this._dateService.onkoLocalDateKahdenValissa(loppuu, tk.alkaa, tk.loppuu) ||
          this._dateService.onkoLocalDateKahdenValissa(tk.alkaa, alkaa, loppuu) ||
          this._dateService.onkoLocalDateKahdenValissa(tk.loppuu, alkaa, loppuu),
      )
    ) {
      return { paallekkainToisenTilikaudenKanssa: true }
    }
    return null
  }

  private _lisaaTasmaytysTilikausirivi(tilikausi: Tilikausi, lukossa: boolean) {
    const jakso = this._dateService.muotoilePaikallinen(tilikausi.alkaa, 'dd.MM.yyyy') + ' - ' + this._dateService.muotoilePaikallinen(tilikausi.loppuu, 'dd.MM.yyyy')

    const { kentta, muutokset } = this._lisaaRivi(
      'tasmaytysTilikaudet',
      this.lomake.tasmaytysTilikaudet,
      { tasmaytysAktiivinen: !tilikausi.omaveroTasmaytysEstetty, tilikaudenJakso: jakso },
      { lukossa },
    )

    muutokset(kentta.tasmaytysAktiivinen, aktiivinen => {
      if (aktiivinen) {
        delete tilikausi.omaveroTasmaytysEstetty
      } else {
        tilikausi.omaveroTasmaytysEstetty = true
      }
    })
    if (lukossa) {
      // Reaktiivisen lomakkeen disable() lähetti kentän arvon tilaajalle.
      asetaArvo(kentta.tasmaytysAktiivinen, kentta.tasmaytysAktiivinen().value())
    }
  }

  private _lisaaVaihdekirjausTilikausirivi(tilikausi: Tilikausi) {
    const jakso = this._dateService.muotoilePaikallinen(tilikausi.alkaa, 'dd.MM.yyyy') + ' - ' + this._dateService.muotoilePaikallinen(tilikausi.loppuu, 'dd.MM.yyyy')

    const { kentta, muutokset } = this._lisaaRivi('vaihdekirjausTilikaudet', this.lomake.vaihdekirjausTilikaudet, {
      kirjauksetMuodostetaan: !tilikausi.tilikaudenVaihteenKirjauksetEstetty,
      tilikaudenJakso: jakso,
    })

    muutokset(kentta.kirjauksetMuodostetaan, muodostetaan => {
      if (muodostetaan) {
        delete tilikausi.tilikaudenVaihteenKirjauksetEstetty
      } else {
        tilikausi.tilikaudenVaihteenKirjauksetEstetty = true
      }
    })
  }

  poistaKkruutujakso(rivi: FieldTree<KkruutujaksoRivi>) {
    const index = this._rivinIndeksi(this.lomake.kkruutukaudet, rivi)
    if (index > -1) {
      this._poistaRivi('kkruutukaudet', this.lomake.kkruutukaudet, index)
      this.asiakas.kkruutujenKaudet.splice(index, 1)
      for (const muu of this.lomake.kkruutukaudet) {
        this._rivinAsetukset(muu).tarkistus?.update(kerta => kerta + 1)
      }
    }
  }

  private _annaTilikausirivi(kausi: Tilikausi): TilikausiRivi {
    const alkaa = this._dateService.localDateToDate(kausi.alkaa)
    const loppuu = this._dateService.localDateToDate(kausi.loppuu)
    kausi.tilikartanProfiili = kausi.tilikartanProfiili || 'maksuperusteinen'
    return { lukittu: !!kausi.lukittu, synkkausLukittu: !!kausi.automationLocked, alkaa, loppuu, tilikartanProfiili: kausi.tilikartanProfiili }
  }

  private _tilausTilikaudelle(kausi: Tilikausi, kentta: FieldTree<TilikausiRivi>, muutokset: <K>(alikentta: FieldTree<K>, kasittelija: (arvo: K) => void) => void, focus: boolean) {
    // kausiGroup.get('kausi').valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe(value => {
    //   kausi.kausi = this.validationService.processValue(value)
    // })
    muutokset(kentta.alkaa, (value: Date) => {
      kausi.alkaa = this._dateService.dateToLocalDate(value)
    })
    muutokset(kentta.loppuu, (value: Date) => {
      kausi.loppuu = this._dateService.dateToLocalDate(value)
    })

    if (focus) {
      setTimeout(() => {
        if (this.tilikausiAlkaaInputit.last) {
          this.tilikausiAlkaaInputit.last.focus()
          this._changeDetectorRef.markForCheck()
        }
      }, 10)
    }
  }

  private _lisaaTilikausirivi(kausi: Tilikausi, focus: boolean, lukitseVanhat: boolean) {
    const pvmLukossa = !!kausi.automationLocked || !!kausi.veroilmoitusSent
    const { kentta, muutokset } = this._lisaaRivi('tilikaudet', this.lomake.tilikaudet, this._annaTilikausirivi(kausi), { pvmLukossa })
    this._tilausTilikaudelle(kausi, kentta, muutokset, focus)
  }

  private _lisaaKirjanpidonTyyppirivi(tyyppi: AsiakkaanKirjanpidonTyyppi) {
    const alkaa = this._dateService.localDateToDate(tyyppi.alkaa)
    const loppuu = this._dateService.localDateToDate(tyyppi.loppuu)
    const { kentta, muutokset } = this._lisaaRivi('kirjanpidonTyypit', this.lomake.kirjanpidonTyypit, { tyyppi: tyyppi.tyyppi ?? null, alkaa, loppuu })

    muutokset(kentta.tyyppi, value => {
      tyyppi.tyyppi = this._validationService.processValue(value)
    })
    muutokset(kentta.alkaa, (value: Date) => {
      tyyppi.alkaa = this._dateService.dateToLocalDate(value)
    })
    muutokset(kentta.loppuu, (value: Date) => {
      tyyppi.loppuu = this._dateService.dateToLocalDate(value)
    })
  }

  private _lisaaKirjanpidonPerusterivi(peruste: AsiakkaanKirjanpidonPeruste, locked: boolean) {
    const alkaa = this._dateService.localDateToDate(peruste.alkaa)
    const loppuu = this._dateService.localDateToDate(peruste.loppuu)

    const { kentta, muutokset } = this._lisaaRivi('kirjanpidonPerusteet', this.lomake.kirjanpidonPerusteet, { peruste: peruste.peruste ?? null, alkaa, loppuu }, { lukossa: locked })

    muutokset(kentta.peruste, value => {
      peruste.peruste = this._validationService.processValue(value)
    })
    muutokset(kentta.alkaa, (value: Date) => {
      peruste.alkaa = value && this._dateService.isValidDate(value) ? this._dateService.dateToLocalDate(this._dateService.kuukaudenEnsimmainen(value)) : null
      if (peruste.alkaa) {
        const index = this._rivinIndeksi(this.lomake.kirjanpidonPerusteet, kentta)
        if (index > 0) {
          const edellinen = this.lomake.kirjanpidonPerusteet[index - 1]
          if (edellinen) {
            asetaArvo(edellinen.loppuu, this._dateService.lisaaPaivia(value, -1))
          }
          // const edellinen = controls[index - 1]
          // const edellinenPeruste = this.asiakas.kirjanpidonPerusteet[index - 1]
          // if (edellinen && edellinenPeruste) {
          //   const edellinenPaiva = this._dateService.lisaaPaivia(value, -1)
          //   edellinenPeruste.loppuu = this._dateService.dateToLocalDate(edellinenPaiva)
          //   edellinen.get('loppuu').setValue(edellinenPaiva)
          // }
        }
      }
    })
    muutokset(kentta.loppuu, (value: Date) => {
      peruste.loppuu = this._dateService.dateToLocalDate(value)
    })
  }

  private _annaAlkaaEnnenLoppuaValidatorTilikausi = (arvo: TilikausiRivi): ValidationErrors | null => {
    const a = arvo?.alkaa
    const l = arvo?.loppuu
    // console.log('asdf', a, l)
    if (l && a && a > l) {
      return { loppuEnnenAlkua: true }
    }
    return null
  }

  private _annaAlkaaEnnenLoppuaValidatorPeruste = (arvo: KirjanpidonPerusteRivi): ValidationErrors | null => {
    const a = arvo?.alkaa
    const l = arvo?.loppuu
    // console.log('asdf', a, l)
    if (l && a && a > l) {
      return { loppuEnnenAlkua: true }
    }
    return null
  }

  private _oikeaAikajarjestysValidatorTilikausi = (rivit: TilikausiRivi[]): ValidationErrors | null => {
    if (rivit?.length > 1) {
      let edellinen: TilikausiRivi = null
      for (const rivi of rivit) {
        if (edellinen) {
          const a = rivi.alkaa
          const l = edellinen.loppuu
          if (l && a && l >= a) {
            return { edellisenLoppu: true }
          }
        }
        edellinen = rivi
      }
    }
    return null
  }

  private _katkeamatonAikajanaValidator = (rivit: KirjanpidonPerusteRivi[]): ValidationErrors | null => {
    if (rivit?.length > 1) {
      let edellinen: KirjanpidonPerusteRivi = null
      for (const rivi of rivit) {
        if (edellinen) {
          const edellinenPeruste = edellinen.peruste
          const peruste = rivi.peruste
          if (edellinenPeruste && peruste && peruste === edellinenPeruste) {
            return { samaPeruste: true }
          }

          const edellinenLoppuu = edellinen.loppuu
          if (!edellinenLoppuu) {
            return { edellisenLoppu: true }
          }

          const nykyinenAlkaa = rivi.alkaa
          if (!nykyinenAlkaa || this._dateService.paiviaValissa(nykyinenAlkaa, edellinenLoppuu) !== 1) {
            return { edellisenLoppu: true }
          }
        }
        edellinen = rivi
      }
    }
    return null
  }

  //   if(control.get('alkaa').value) {
  //   const controls = this.annaKirjanpidonPerusteetControllit()
  //   const index = controls.indexOf(control)
  //   if (index > 0) {
  //     const edellinen = controls[index - 1]
  //     if (edellinen) {
  //       const edellinenControl = edellinen.get('loppuu')
  //       if (!edellinenControl.value) {
  //         return { edellinen: true }
  //       }
  //       console.log(this._dateService.paiviaValissa(control.get('alkaa').value, edellinenControl.value))
  //       if (this._dateService.paiviaValissa(control.get('alkaa').value, edellinenControl.value) !== 1) {
  //         return { edellinen: true }
  //       }
  //     }
  //   }
  // }
  // return null
  //   }

  private _lisaaAlvIlmoitusjaksorivi(
    jakso: AlvIlmoitusjaksoAikajaksolla,
    index: number,
    jaksot: AlvIlmoitusjaksoAikajaksolla[],
    viimeisinAlvIlmoitus: AlvIlmoitus,
    focus: boolean,
    lukitseVanhat: boolean,
    aikaisinMahdollinenPaivamaara: Date,
    punakynaActivated: boolean,
  ) {
    const paiva = this._dateService.localDateToDate(jakso.voimassaAlkaen)
    const viimeisimmanAlvIlmonLoppuPvmAsDate = this._dateService.localDateToDate(viimeisinAlvIlmoitus ? viimeisinAlvIlmoitus.endDate : { year: 1980, month: 1, day: 1 })
    // console.log('PÄIVÄ', jakso.voimassaAlkaen, paiva, this._dateService.kuukausiaValissa(paiva, viimeisimmanAlvIlmonLoppuPvmAsDate))
    const pvmLukossa = !punakynaActivated && lukitseVanhat && this._dateService.kuukausiaValissa(paiva, viimeisimmanAlvIlmonLoppuPvmAsDate) < 1
    const tyyppiSaaOllaAuki = index === 0 && jaksot.length === 1 && !viimeisinAlvIlmoitus
    const tyyppiLukossa = !punakynaActivated && !tyyppiSaaOllaAuki && pvmLukossa
    const { kentta, muutokset } = this._lisaaRivi(
      'alvIlmoitusjaksot',
      this.lomake.alvIlmoitusjaksot,
      { jakso: jakso.alvIlmoitusjakso ?? null, voimassaAlkaen: paiva },
      { pvmLukossa, tyyppiLukossa, punakynaActivated, aikaisinMahdollinenPaivamaara },
    )

    muutokset(kentta.voimassaAlkaen, (value: Date) => {
      jakso.voimassaAlkaen = this._dateService.dateToLocalDate(value)
    })
    muutokset(kentta.jakso, value => {
      jakso.alvIlmoitusjakso = value
    })

    if (focus) {
      setTimeout(() => {
        if (this.alvIlmoitusjaksoSelectit.last) {
          this.alvIlmoitusjaksoSelectit.last.focus()
          this._changeDetectorRef.markForCheck()
        }
      }, 10)
    }
  }

  annaUusiAlvIlmoitusjaksoAikajaksolla(): AlvIlmoitusjaksoAikajaksolla {
    return {
      alvIlmoitusjakso: null,
      muutosPvm: this._timestampService.now(),
      voimassaAlkaen: null,
    }
  }

  poistaAlvIlmoitusjaksorivi(rivi: FieldTree<AlvIlmoitusjaksoRivi>) {
    const index = this._rivinIndeksi(this.lomake.alvIlmoitusjaksot, rivi)
    if (rivi.jakso().disabled() || index < 1) {
      return
    }
    this._poistaRivi('alvIlmoitusjaksot', this.lomake.alvIlmoitusjaksot, index)
    this.asiakas.alvIlmoitusjaksot.splice(index, 1)
  }

  async lisaaAlvIlmoitusjaksorivi() {
    const viimeisinAlvIlmoitus = await firstValueFrom(this.viimeisinAlvIlmoitusObservable)
    const punakynaActivated = await firstValueFrom(this.punakynaActiveSubject)
    const aikaisinMahdollinenPaivamaara = this._annaAikaisinMahdollinenPaivamaara(viimeisinAlvIlmoitus)

    if (!this.asiakas.alvIlmoitusjaksot) {
      this.asiakas.alvIlmoitusjaksot = []
    }

    const jakso = this.annaUusiAlvIlmoitusjaksoAikajaksolla()
    this._lisaaAlvIlmoitusjaksorivi(jakso, this.asiakas.alvIlmoitusjaksot.length, this.asiakas.alvIlmoitusjaksot, null, true, false, aikaisinMahdollinenPaivamaara, punakynaActivated)

    this.asiakas.alvIlmoitusjaksot.push(jakso)
  }

  private edellisenAlvjaksonKuukausiValidator = (arvo: Date, index: number): ValidationErrors | null => {
    if (arvo) {
      const rivit = this.lomake.alvIlmoitusjaksot().value()
      if (index > 0) {
        const edellinen = rivit[index - 1]

        if (edellinen) {
          const edellinenVoimassaAlkaen = edellinen.voimassaAlkaen
          if (edellinenVoimassaAlkaen) {
            const currentDateAsLocal = this._dateService.dateToLocalDate(arvo)

            // Validoi, ettei tämä ala ennen edellistä
            // console.log('Validoidaan nykyinen: ', arvo)
            // console.log('Validoidaan edellinen:', edellinenVoimassaAlkaen)
            // console.log('Kuukausia välissä:', this._dateService.kuukausiaValissa(arvo, edellinenVoimassaAlkaen))
            if (this._dateService.kuukausiaValissa(arvo, edellinenVoimassaAlkaen) < 1) {
              return { edellinen: true }
            }

            // Tässä käsitellään kaikki jaksojen vaihtojen väliset säännöt
            const edellinenJakso = edellinen.jakso
            if (edellinenJakso) {
              const nykyinenJakso = rivit[index].jakso

              // Vaihdetaan 1kk tai 3kk -> 12kk
              if ((edellinenJakso === AlvIlmoitusjakso.KK1 || edellinenJakso === AlvIlmoitusjakso.KK3) && nykyinenJakso === AlvIlmoitusjakso.KK12) {
                const seuraavanVuodenAlku: LocalDate = { year: edellinenVoimassaAlkaen.getFullYear() + 1, month: 1, day: 1 }
                if (this._dateService.kuukausiaValissa(arvo, this._dateService.localDateToDate(seuraavanVuodenAlku)) < 0) {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  return { a1_or_3_to12: true }
                }
                if (currentDateAsLocal.month !== 1) {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  return { b1_or_3_to12: true }
                }
              }

              // Vaihdetaan 1kk -> 3kk
              if (edellinenJakso === AlvIlmoitusjakso.KK1 && nykyinenJakso === AlvIlmoitusjakso.KK3) {
                const asLocal: LocalDate = this._dateService.dateToLocalDate(edellinenVoimassaAlkaen)
                asLocal.day = 1
                if (asLocal.month === 1 || asLocal.month === 2 || asLocal.month === 3) {
                  asLocal.month = 4
                } else if (asLocal.month === 4 || asLocal.month === 5 || asLocal.month === 6) {
                  asLocal.month = 7
                } else if (asLocal.month === 7 || asLocal.month === 8 || asLocal.month === 9) {
                  asLocal.month = 10
                } else if (asLocal.month === 10 || asLocal.month === 11 || asLocal.month === 12) {
                  asLocal.month = 1
                  asLocal.year = asLocal.year + 1
                }
                if (this._dateService.kuukausiaValissa(arvo, this._dateService.localDateToDate(asLocal)) < 0) {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  return { a1_to_3: true }
                }
                if (currentDateAsLocal.month !== 1 && currentDateAsLocal.month !== 4 && currentDateAsLocal.month !== 7 && currentDateAsLocal.month !== 10) {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  return { b1_to_3: true }
                }
              }

              // Vaihdetaan 12kk tai 3kk -> 1kk
              if ((edellinenJakso === AlvIlmoitusjakso.KK12 || edellinenJakso === AlvIlmoitusjakso.KK3) && nykyinenJakso === AlvIlmoitusjakso.KK1) {
                // Aikaisintaan seuraavana kuuna, joka on sama kuin perusvalidaattori
              }

              // Vaihdetaan 12kk -> 3kk
              if (edellinenJakso === AlvIlmoitusjakso.KK12 && nykyinenJakso === AlvIlmoitusjakso.KK3) {
                const asLocal: LocalDate = this._dateService.dateToLocalDate(edellinenVoimassaAlkaen)
                asLocal.day = 1
                if (asLocal.month === 1 || asLocal.month === 2 || asLocal.month === 3) {
                  asLocal.month = 4
                } else if (asLocal.month === 4 || asLocal.month === 5 || asLocal.month === 6) {
                  asLocal.month = 7
                } else if (asLocal.month === 7 || asLocal.month === 8 || asLocal.month === 9) {
                  asLocal.month = 10
                } else if (asLocal.month === 10 || asLocal.month === 11 || asLocal.month === 12) {
                  asLocal.month = 1
                  asLocal.year = asLocal.year + 1
                }
                if (this._dateService.kuukausiaValissa(arvo, this._dateService.localDateToDate(asLocal)) < 0) {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  return { a12_to_3: true }
                }
                if (currentDateAsLocal.month !== 1 && currentDateAsLocal.month !== 4 && currentDateAsLocal.month !== 7 && currentDateAsLocal.month !== 10) {
                  // eslint-disable-next-line @typescript-eslint/naming-convention
                  return { b12_to_3: true }
                }
              }
            }
          }
        }
      }
    }
    return null
  }

  // 12kk -> 1kk, 3kk -> 1kk: seuraavan kuukauden 1.pv HUOM!!! TÄMÄN PITÄÄ KATKAISTA PITEMPI JAKSO MYÖS ALV-ILMOITUKSELLA!!!!!
  // 12kk -> 3kk: seuraava näistä: 1.1, 1.4, 1.7, 1.10
  // 1kk -> 3kk tai 12kk, 3kk -> 12kk: seuraavan vuoden 1.1

  private edellinenAlvJaksoValidator = (arvo: AlvIlmoitusjakso, index: number): ValidationErrors | null => {
    if (arvo) {
      const rivit = this.lomake.alvIlmoitusjaksot().value()
      if (index > 0) {
        const edellinen = rivit[index - 1]
        if (edellinen) {
          const edellinenJakso = edellinen.jakso
          if (edellinenJakso) {
            if (arvo === edellinenJakso) {
              return { samajakso: true }
            }
          }
        }
      }
    }
    return null
  }

  private annaUusiKausi(previous?: Tilikausi): Tilikausi {
    if (previous) {
      const previousLengthIsOneYear = this._dateService.kuukausiaValissaPaikallinen(this._dateService.lisaaPaiviaPaikallinen(previous.loppuu, 1), previous.alkaa) === 12
      const alkaa = this._dateService.lisaaPaiviaPaikallinen(previous.loppuu, 1)
      return {
        avain: this._firebase.firestoreCreateId(),
        alkaa: alkaa,
        loppuu: previousLengthIsOneYear ? this._dateService.lisaaPaiviaPaikallinen(this._dateService.lisaaKuukausiaPaikallinen(alkaa, 12), -1) : null,
        tilikartanProfiili: previous.tilikartanProfiili || 'maksuperusteinen',
      }
    }
    return {
      avain: this._firebase.firestoreCreateId(),
      alkaa: null,
      loppuu: null,
      tilikartanProfiili: 'maksuperusteinen',
    }
  }

  poistaTilikausi(rivi: FieldTree<TilikausiRivi>) {
    const index = this._rivinIndeksi(this.lomake.tilikaudet, rivi)
    if (index > -1) {
      this._poistaRivi('tilikaudet', this.lomake.tilikaudet, index)
      this.asiakas.tilikaudet.splice(index, 1)
    }
  }

  luoUusiTilikausi() {
    const edellinenTilikausi = this.asiakas.tilikaudet?.sort((a, b) => this._dateService.localDateToNumber(b.alkaa) - this._dateService.localDateToNumber(a.alkaa))?.[0]

    this._tempKausi = this.annaUusiKausi(edellinenTilikausi)
    const pvmLukossa = !!this._tempKausi.automationLocked || !!this._tempKausi.veroilmoitusSent
    this.uusiTilikausiLomake = form(
      signal<TilikausiRivi>(this._annaTilikausirivi(this._tempKausi)),
      polku => {
        disabled(polku.synkkausLukittu)
        disabled(polku.alkaa, () => pvmLukossa)
        disabled(polku.loppuu, () => pvmLukossa)
        disabled(polku.tilikartanProfiili)
        required(polku.alkaa)
        required(polku.loppuu)
        validate(polku, ({ value }) => signaaliVirheet(this._onkoPaallekkainToisenTilikaudenKanssaValidator(value())))
      },
      { injector: this._injector },
    )
    const tilaukset = new Subscription()
    this._tilausTilikaudelle(
      this._tempKausi,
      this.uusiTilikausiLomake,
      (kentta, kasittelija) => {
        tilaukset.add(arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this._ngUnsubscribe)).subscribe(kasittelija))
      },
      true,
    )

    this.setNewTilikausiPending(true)
  }

  async tallennaUusiTilikausi() {
    if (!this.uusiTilikausiLomake().valid()) {
      this.uusiTilikausiLomake().markAsTouched()
      return
    }

    if (this.saveInFlight) {
      return
    }

    this.saveInFlight = true

    this._ladataanService.aloitaLataaminen()

    try {
      const asiakas = await firstValueFrom(this._asiakasService.annaAsiakasObservable(this.asiakas.avain))
      await this._asiakasService.lisaaTilikausi(asiakas, this._tempKausi)

      const batch = this._firebase.firestoreBatch()
      const uri = 'tyojono/' + asiakas.avain + '/luo-tilikaudet-ja-kuukausiruudut/' + this._firebase.firestoreCreateId()
      const data: AsiakkaidenTilikausienLuontityodata = {
        tiettyAsiakas: asiakas.avain,
      }

      batch.set(uri, data)
      await batch.commit()

      nollaa(this.uusiTilikausiLomake, { lukittu: null, synkkausLukittu: null, alkaa: null, loppuu: null, tilikartanProfiili: null })
      this.setNewTilikausiPending(false)

      this._asiakasService.asetaNykyinenAsiakas(this.asiakas)
      this._snackbar.open('Uusi tilikausi tallennettiin onnistuneesti.', 'OK', { duration: 5000, verticalPosition: 'top' })
    } catch (err) {
      this._errorHandler.handleError(err)
      this._snackbar.open('TALLENNUS EPÄONNISTUI. Kerro terveiset ylläpidolle.', 'Pilalla', { duration: 10000, verticalPosition: 'top' })
    } finally {
      this.saveInFlight = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  lisaaKirjanpidonTyyppi() {
    const tyyppi: AsiakkaanKirjanpidonTyyppi = {
      alkaa: null,
      loppuu: null,
      tyyppi: KirjanpidonTyyppi.KAHDENKERTAINEN,
    }
    this._lisaaKirjanpidonTyyppirivi(tyyppi)

    if (!this.asiakas.kirjanpidonTyypit) {
      this.asiakas.kirjanpidonTyypit = []
    }
    this.asiakas.kirjanpidonTyypit.push(tyyppi)
  }

  lisaaKirjanpidonPeruste() {
    if (!this.asiakas.kirjanpidonPerusteet) {
      this.asiakas.kirjanpidonPerusteet = []
    }

    const peruste: AsiakkaanKirjanpidonPeruste = {
      alkaa: null,
      loppuu: null,
      peruste: KirjanpidonPeruste.MAKSU,
    }

    this._lisaaKirjanpidonPerusterivi(peruste, this.asiakas.kirjanpidonPerusteet.length < 1)

    this.asiakas.kirjanpidonPerusteet.push(peruste)
  }

  private _annaMinimiMuutokseDate(index: number, aikaisinMahdollinenPaivamaara: Date): Date {
    const rivit = this.lomake.alvIlmoitusjaksot().value()
    if (index > 0) {
      const edellinen = rivit[index - 1]
      if (edellinen) {
        const edellinenVoimassaAlkaen = edellinen.voimassaAlkaen
        if (edellinenVoimassaAlkaen) {
          const edellisenPaivamaaranPerusteella = this._dateService.lisaaKuukausia(edellinenVoimassaAlkaen, 1)

          let jaksonPerusteella: Date = null
          // Tässä käsitellään kaikki jaksojen vaihtojen väliset säännöt
          const edellinenJakso = edellinen.jakso
          if (edellinenJakso) {
            const nykyinenJakso = rivit[index].jakso

            // Vaihdetaan 1kk pidempään
            if (edellinenJakso === AlvIlmoitusjakso.KK1 && (nykyinenJakso === AlvIlmoitusjakso.KK12 || nykyinenJakso === AlvIlmoitusjakso.KK3)) {
              const seuraavanVuodenAlku: LocalDate = { year: edellinenVoimassaAlkaen.getFullYear() + 1, month: 1, day: 1 }
              jaksonPerusteella = this._dateService.localDateToDate(seuraavanVuodenAlku)
            }

            // Vaihdetaan 3kk pidempään
            if (edellinenJakso === AlvIlmoitusjakso.KK3 && nykyinenJakso === AlvIlmoitusjakso.KK12) {
              const seuraavanVuodenAlku: LocalDate = { year: edellinenVoimassaAlkaen.getFullYear() + 1, month: 1, day: 1 }
              jaksonPerusteella = this._dateService.localDateToDate(seuraavanVuodenAlku)
            }

            // Vaihdetaan 12kk tai 3kk 1kk
            if ((edellinenJakso === AlvIlmoitusjakso.KK12 || edellinenJakso === AlvIlmoitusjakso.KK3) && nykyinenJakso === AlvIlmoitusjakso.KK1) {
              // Aikaisintaan seuraavana kuuna, joka on sama kuin perusvalidaattori
            }

            // Vaihdetaan 12kk -> 3kk
            if (edellinenJakso === AlvIlmoitusjakso.KK12 && nykyinenJakso === AlvIlmoitusjakso.KK3) {
              const asLocal: LocalDate = this._dateService.dateToLocalDate(edellinenVoimassaAlkaen)
              asLocal.day = 1
              if (asLocal.month === 1 || asLocal.month === 2 || asLocal.month === 3) {
                asLocal.month = 4
              } else if (asLocal.month === 4 || asLocal.month === 5 || asLocal.month === 6) {
                asLocal.month = 7
              } else if (asLocal.month === 7 || asLocal.month === 8 || asLocal.month === 9) {
                asLocal.month = 10
              } else if (asLocal.month === 10 || asLocal.month === 11 || asLocal.month === 12) {
                asLocal.month = 1
                asLocal.year = asLocal.year + 1
              }
              jaksonPerusteella = this._dateService.localDateToDate(asLocal)
            }
          }
          return this._dateService.max([aikaisinMahdollinenPaivamaara, jaksonPerusteella, edellisenPaivamaaranPerusteella].filter(Boolean))
        }
      }
    }

    return aikaisinMahdollinenPaivamaara
  }

  // chosenMonthHandler(normalizedMonth: Date, formGroup: FormGroup, datepicker: MatDatepicker<Date>) {
  //   const control = formGroup.get('voimassaAlkaen') as FormControl
  //   const minDate = this.annaMinimiPaivamaara(control)
  //   if (this._dateService.kuukausiaValissa(normalizedMonth, minDate) > -1) {
  //     control.setValue(normalizedMonth)
  //     control.markAsTouched()
  //   }
  //   datepicker.close()
  // }

  private saveInFlight = false

  async tallennaEnsimmainenOikeaVastuukirjanpitaja() {
    const salesListaltaKirjanpitajalle = this.salesListaltaKirjanpitajalle
    if (!salesListaltaKirjanpitajalle) {
      return
    }

    const voimassaoloAlkaa = salesListaltaKirjanpitajalle.voimassaoloAlkaa().value()
    if (!voimassaoloAlkaa) {
      return
    }

    // Uusi kirjanpitäjä
    const uusiKirjanpitajaAvain = salesListaltaKirjanpitajalle.kirjanpitaja().value()
    if (!uusiKirjanpitajaAvain || uusiKirjanpitajaAvain === this.SALES_KIRJANPITAJA_AVAIN) {
      return
    }

    if (this.saveInFlight) {
      return
    }
    this.saveInFlight = true

    const kirjanpitajanTiimi = await this._kirjanpitajanTiimiService.annaKirjanpitajanEnsimmainenTiimiPrimaariTiedolla(uusiKirjanpitajaAvain, this._dateService.currentNumberDate())
    if (!kirjanpitajanTiimi?.tiimi?.avain) {
      this.saveInFlight = false
      this._snackbar.open('Valitulle kirjanpitäjälle ei löytynyt tiimiä, joten ensimmäistä vastuukirjanpitäjää ei voi vaihtaa. Esimerkiksi Poistuneet asiakkaat -tunnus ei kuulu tiimiin.', 'OK', {
        duration: 10000,
        verticalPosition: 'top',
      })
      return
    }

    const dialogData: AreYouSureDialogData = {
      header: 'Oletko varma että haluat vaihtaa ensimmäisen kirjanpitäjän?',
      rightAction: 'Vaihda',
      text: 'Ensimmäisen vastuukirjanpitäjän valitseminen onnistuu vain yhden kerran. Laskujen tilastot vaihdetaan uudelle kirjanpitäjälle.',
    }

    const isSure = await firstValueFrom(this._dialog.open<AreYouSureDialog, AreYouSureDialogData>(AreYouSureDialog, { data: dialogData }).afterClosed())
    if (!isSure) {
      this.saveInFlight = false
      return
    }

    const batch = this._firebase.firestoreBatch()
    try {
      this._ladataanService.aloitaLataaminen()

      // Pyynnön tekijä
      const paivittaja = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()

      // Tallenna jakolistan tiedot
      this.asiakas.siirrettyJakolistalta = {
        salesAlkaa: this.salesAlkaaPaiva,
        siirtoPaiva: this._dateService.currentLocalDate(),
      }

      // Vaihda uusi kirjanpitäjä
      this.asiakas.kirjanpitajat[0] = {
        kirjanpitajanAvain: uusiKirjanpitajaAvain,
        voimassaoloAlkaa: this._dateService.dateToLocalDate(voimassaoloAlkaa),
      }
      this._changeDetectorRef.markForCheck()

      // Tallenna asiakas
      await this._asiakasService.lisaaAsiakasBatchiin(this.asiakas, paivittaja, 'paivita-tilikausiin-perustuvat-tiedot', false, true, batch)

      // Lisää tilastojen päivitys
      const uri = this._asiakasUriService.annaTilastojenPaivitysTyoUri(this.asiakas.avain, this._firebase.firestoreCreateId())

      const data: AsiakkaanTilastojenPaivitysTyojonotiedot = {
        asiakasAvain: this.asiakas.avain,
        vanhaKirjanpitajaAvain: this.SALES_KIRJANPITAJA_AVAIN,
        uusiKirjanpitajaAvain: uusiKirjanpitajaAvain,
        uusiKirjanpitajanTiimi: kirjanpitajanTiimi.tiimi.avain,
        paivittaja: paivittaja.uid,
        created: this._timestampService.now(),
      }
      batch.set(uri, data)

      await batch.commit()
      this._snackbar.open('Asiakkaan tiedot tallennettiin ja tilastojen siirto lisättiin käsittelyjonoon', 'OK', { duration: 5000, verticalPosition: 'top' })
      this._ladataanService.lopetaLataaminen()
      this.saveInFlight = false
    } catch (err) {
      this._ladataanService.lopetaLataaminen()
      this.saveInFlight = false
      this._errorHandler.handleError(err)
      this._snackbar.open('Työn lisäys epäonnistui. Kerro terveiset ylläpidolle.', 'Pilalla', { duration: 10000, verticalPosition: 'top' })
    }
  }

  async save() {
    if (this.lomake().invalid()) {
      // console.log(this.form.errors)
      this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
      this.paivitaValidaatiot = {}
      return
    }

    if (this.saveInFlight) {
      return
    }
    this.saveInFlight = true

    this._ladataanService.aloitaLataaminen()

    const batch = this._firebase.firestoreBatch()
    if (this._ovatkoKuukausijaksotMuuttuneet(this.asiakas, this._originalAsiakas)) {
      const uri = 'tyojono/' + this.asiakas.avain + '/luo-tilikaudet-ja-kuukausiruudut/' + this._firebase.firestoreCreateId()
      const data: AsiakkaidenTilikausienLuontityodata = {
        tiettyAsiakas: this.asiakas.avain,
      }
      batch.set(uri, data)
    }

    const kirjanpitajanTiedot = await this._kirjautunutKayttajaService.getKirjanpitajanTiedot()
    await this._asiakasService.lisaaAsiakasBatchiin(this.asiakas, kirjanpitajanTiedot, 'paivita-tilikausiin-perustuvat-tiedot', false, true, batch)

    this._lisaaSelvittelyreskontranKytkentaBatchiin(batch)

    batch
      .commit()
      .then(() => {
        this.saveInFlight = false
        this._asiakasService.asetaNykyinenAsiakas(this.asiakas)
        this._snackbar.open('Kirjanpitotiedot tallennettiin onnistuneesti.', 'OK', { duration: 5000, verticalPosition: 'top' })
        this._ladataanService.lopetaLataaminen()
      })
      .catch(err => {
        this.saveInFlight = false
        this._ladataanService.lopetaLataaminen()
        this._errorHandler.handleError(err)
        this._snackbar.open('TALLENNUS EPÄONNISTUI. Kerro terveiset ylläpidolle.', 'Pilalla', { duration: 10000, verticalPosition: 'top' })
      })
  }

  private _lisaaSelvittelyreskontranKytkentaBatchiin(batch: IFirestoreBatch) {
    const selvittelyEnnen = this._asiakasJaettuService.onkoSelvittelyLukituillaKaytossa(this._originalAsiakas)
    const selvittelyNyt = this._asiakasJaettuService.onkoSelvittelyLukituillaKaytossa(this.asiakas)
    if (selvittelyEnnen === selvittelyNyt) {
      return
    }
    const tyojonoUri = this._asiakasUriService.annaSelvittelyreskontranKytkentaTyoUri(this.asiakas.avain, this._firebase.firestoreCreateId())
    const tyodata: SelvittelyreskontranKytkentaTyoData = {
      asiakasAvain: this.asiakas.avain,
      aloitettu: this._timestampService.now(),
    }
    batch.set(tyojonoUri, tyodata)
  }

  private _ovatkoKuukausijaksotMuuttuneet(asiakas1: Asiakas, asiakas2: Asiakas): boolean {
    if (asiakas1.kkruutujenKaudet?.length !== asiakas2.kkruutujenKaudet?.length) {
      return true
    } else if (asiakas1.kkruutujenKaudet && asiakas2.kkruutujenKaudet) {
      let i = 0
      for (const eka of asiakas1.kkruutujenKaudet) {
        const toka = asiakas2.kkruutujenKaudet[i]
        if (
          (eka.alkaa && toka.alkaa && this._dateService.compareLocalDates(eka.alkaa, '!=', toka.alkaa)) ||
          (eka.alkaa && !toka.alkaa) ||
          (!eka.alkaa && toka.alkaa) ||
          (eka.loppuu && toka.loppuu && this._dateService.compareLocalDates(eka.loppuu, '!=', toka.loppuu)) ||
          (eka.loppuu && !toka.loppuu) ||
          (!eka.loppuu && toka.loppuu)
        ) {
          return true
        }
        i++
      }
    }
    return false
  }

  annaProfiilinNimi(tunnus: PaatilikartanProfiili['avain']): string {
    return this.kaikkiTilikartanProfiilit.find(p => p.tunnus === tunnus)?.nimi || tunnus
  }

  avaaProfiiliDialogi(index: number) {
    const tilikausi = this.asiakas.tilikaudet[index]
    const data: TilikartanProfiiliVaihdaDialogData = {
      asiakas: this.asiakas,
      tilikausi: tilikausi,
    }
    this._dialog
      .open<TilikartanProfiiliVaihdaDialog, TilikartanProfiiliVaihdaDialogData, boolean>(TilikartanProfiiliVaihdaDialog, { data, autoFocus: false })
      .afterClosed()
      .pipe(take(1))
      .subscribe(muutettiin => {
        if (muutettiin) {
          asetaArvo(this.lomake.tilikaudet[index].tilikartanProfiili, tilikausi.tilikartanProfiili, { ilmoita: false })
        }
      })
  }

  avaaTilikausi(index: number) {
    const tilikausi = this.asiakas.tilikaudet[index]

    if (!this._asiakasJaettuService.voikoTilikaudenLukituksenAvata(tilikausi, this.asiakas)) {
      // Lukitun tilikauden voi avata vain jos sitä myöhempää lukittua tilikautta ei ole.
      console.error(`Asiakkaan ${this.asiakas.avain} lukittua tilikautta ${tilikausi.avain} ei voida avata, koska myöhempi tilikausi on lukittu.`)
      const dialogData: InfoDialogData = {
        header: 'Lukittua tilikautta ei voida avata',
        text: 'Lukitun tilikauden voi avata vain, jos sitä myöhempää lukittua tilikautta ei ole. ',
      }
      this._dialog.open(InfoDialog, { data: dialogData })
      return
    }

    const data: AreYouSureDialogData = {
      header: 'Haluatko avata tilikauden uudelleen muokattavaksi?',
      rightAction: 'Avaa',
      text: 'Tilikausi voidaan myöhemmin lukita uudelleen.',
    }
    const dialogHandle = this._dialog.open(AreYouSureDialog, { data: data })
    dialogHandle
      .afterClosed()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(result => {
        if (result) {
          this._ladataanService.aloitaLataaminen()
          const tyoavain = this._firebase.firestoreCreateId()
          const uri = this._kirjanpitoUriService.annaTyojonoKirjanpitoTilikausiValmiiksiUri(this.asiakas.avain, tyoavain)
          const tyojono: KirjanpitoMerkitseTilikausiValmiiksiTyojonotiedot = {
            asiakasAvain: this.asiakas.avain,
            tilikausiAvain: this.asiakas.tilikaudet[index].avain,
            toiminto: 'poista-valmismerkinta',
            estaAutomaatiolukitus: true,
          }
          this._firebase.firestoreSetData(uri, tyojono)
          // console.log('Avataan tilikausi ', tilikausi.avain, tilikausi.alkaa, tilikausi.loppuu)
          setTimeout(() => {
            this._ladataanService.lopetaLataaminen()
          }, 4000)
        }
        //  else {
        // console.log('Cancel')
        // }
      })
  }

  lukitseTilikausi(index: number) {
    const tilikausi = this.asiakas.tilikaudet[index]
    const data: AreYouSureDialogData = {
      header: 'Haluatko varmasti lukita tilikauden?',
      rightAction: 'Lukitse',
      text: 'Tilikautta tai sen kirjauksia ja vientejä ei voi enää lukitsemisen jälkeen muokata ja aineistosta taltioidaan uusi versio. Tilikausi merkitään kokonaan käsitellyksi.',
    }
    const dialogHandle = this._dialog.open(AreYouSureDialog, { data: data })
    dialogHandle
      .afterClosed()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(result => {
        if (result) {
          this._ladataanService.aloitaLataaminen()
          const tyoavain = this._firebase.firestoreCreateId()
          const uri = this._kirjanpitoUriService.annaTyojonoKirjanpitoTilikausiValmiiksiUri(this.asiakas.avain, tyoavain)
          const tyojono: KirjanpitoMerkitseTilikausiValmiiksiTyojonotiedot = {
            asiakasAvain: this.asiakas.avain,
            tilikausiAvain: this.asiakas.tilikaudet[index].avain,
            toiminto: 'merkitse-valmiiksi',
            estaAutomaatiolukitus: true,
          }
          this._firebase.firestoreSetData(uri, tyojono)
          // console.log('Lukitaan tilikausi ', tilikausi.avain, tilikausi.alkaa, tilikausi.loppuu)
          setTimeout(() => {
            this._ladataanService.lopetaLataaminen()
          }, 1500)
        }
        //  else {
        //   console.log('Cancel')
        // }
      })
  }

  async avaaSynkkaus(index: number) {
    if (!this.onkoDevaajaTaiKayttotuki()) {
      return
    }

    const data: AreYouSureDialogData = {
      header: 'Haluatko avata tilikauden synkkaukset uudelleen?',
      rightAction: 'Avaa',
      text: 'Synkkaus voidaan myöhemmin lukita uudelleen. Muista tallentaa lomake sen jälkeen, kun painat Avaa',
    }

    const isSure = await firstValueFrom(this._dialog.open<AreYouSureDialog, AreYouSureDialogData>(AreYouSureDialog, { data: data }).afterClosed())
    if (!isSure) {
      return
    }

    const tilikausi = this.asiakas.tilikaudet?.[index]
    if (!tilikausi) {
      throw new Error('Ei tilikautta indeksillä ' + index)
    }
    delete tilikausi.automationLocked
  }

  async lukitseSynkkaus(index: number) {
    if (!this.onkoDevaajaTaiKayttotuki()) {
      return
    }

    const data: AreYouSureDialogData = {
      header: 'Haluatko varmasti lukita synkkauksen?',
      rightAction: 'Lukitse',
      text: 'Kirjanpitoon ei ladata tälle tilikaudelle lukituksen jälkeen enää tilitapahtumia eikä palkkatietoja. Muista tallentaa lomake sen jälkeen, kun painat Lukitse.',
    }

    const isSure = await firstValueFrom(this._dialog.open<AreYouSureDialog, AreYouSureDialogData>(AreYouSureDialog, { data: data }).afterClosed())
    if (!isSure) {
      return
    }

    const tilikausi = this.asiakas.tilikaudet?.[index]
    if (!tilikausi) {
      throw new Error('Ei tilikautta indeksillä ' + index)
    }
    tilikausi.automationLocked = true
  }

  lisaaVastaanottaja() {
    this.lomake.vastaanottaja().markAsDirty()
    this.lomake.vastaanottaja().markAsTouched()
    this.lisaaEmail(this.lomake.vastaanottaja)
  }
  poistaVastaanottaja(vastaanottaja: string): void {
    if (!this.asiakas.kirjanpitoviestienLisavastaanottajat) {
      this.asiakas.kirjanpitoviestienLisavastaanottajat = []
    }
    const index = this.asiakas.kirjanpitoviestienLisavastaanottajat.indexOf(vastaanottaja)
    if (index >= 0) {
      this.asiakas.kirjanpitoviestienLisavastaanottajat.splice(index, 1)
      if (this.asiakas.kirjanpitoviestienLisavastaanottajat.length < 1) {
        delete this.asiakas.kirjanpitoviestienLisavastaanottajat
      }
    }
  }
  private lisaaEmail(field: FieldTree<string>) {
    if ((field().value() || '').trim()) {
      let arvo = field().value().trim()
      arvo = arvo.replace(/,/g, '').trim()
      if (!Validators.email({ value: arvo } as AbstractControl)) {
        if (!this.asiakas.kirjanpitoviestienLisavastaanottajat) {
          this.asiakas.kirjanpitoviestienLisavastaanottajat = []
        }
        this.asiakas.kirjanpitoviestienLisavastaanottajat.push(arvo)
        asetaArvo(field, '')
      }
    }
  }

  setNewTilikausiPending(val: boolean) {
    this.newTilikausiPending.set(val)
  }
}
